import type { ResumeDto } from "@izicvs/dto";
import { Button } from "@izicvs/ui";
import { pageSizeMap } from "@izicvs/utils";
import { t } from "@lingui/macro";
import { CircleNotch, FilePdf } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import type { LoaderFunction } from "react-router";
import { Link, redirect, useLoaderData } from "react-router";

import { Icon } from "@/client/components/icon";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { queryClient } from "@/client/libs/query-client";
import { localizeResumeSectionNames } from "@/client/pages/builder/sidebars/left/sections/shared/section-label";
import { findResumeByUsernameSlug, usePrintResume } from "@/client/services/resume";

const openInNewTab = (url: string) => {
  const win = window.open(url, "_blank");
  if (win) win.focus();
};

export const PublicResumePage = () => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { printResume, loading } = usePrintResume();

  const { id, title, data: resume } = useLoaderData();
  const format = resume.metadata.page.format as keyof typeof pageSizeMap;

  // Real page size in px (from the artboard), and the scale that fits it to the
  // available width, so the fixed-size A4/Letter page is readable on mobile.
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);

  const payload = useMemo(() => localizeResumeSectionNames(resume), [resume]);

  // Deliver the resume to the artboard, retrying until it acknowledges with its
  // size. A single send can land before the artboard's listener exists (notably
  // on Safari), which silently dropped the resume and left the page blank.
  useEffect(() => {
    if (dimensions.width) return;

    const send = () => {
      frameRef.current?.contentWindow?.postMessage(
        { type: "SET_RESUME", payload },
        window.location.origin,
      );
    };

    send();
    const interval = setInterval(send, 300);
    return () => {
      clearInterval(interval);
    };
  }, [dimensions.width, payload]);

  // Receive the rendered page size from the artboard. Re-attach on load in case
  // the frame's window is replaced during navigation.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "PAGE_LOADED") {
        const { width, height } = event.data.payload;
        if (width && height) setDimensions({ width, height });
      }
    };

    const attach = () => frameRef.current?.contentWindow?.addEventListener("message", handleMessage);
    attach();

    const frame = frameRef.current;
    frame?.addEventListener("load", attach);

    return () => {
      frameRef.current?.contentWindow?.removeEventListener("message", handleMessage);
      frame?.removeEventListener("load", attach);
    };
  }, []);

  // Keep the page scaled to fit the viewport width (never upscale past 1).
  useEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;

    const update = () => {
      if (!dimensions.width) return;
      setScale(Math.min(1, element.clientWidth / dimensions.width));
    };
    update();

    const observer = new ResizeObserver(update);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [dimensions.width]);

  const onDownloadPdf = async () => {
    const { url } = await printResume({ id });

    openInNewTab(url);
  };

  return (
    <div className="min-h-screen pb-24">
      <Helmet>
        <title>
          {title} - {t`Izicvs`}
        </title>
      </Helmet>

      <div ref={wrapperRef} className="w-full">
        <div
          className="relative z-50 mx-auto my-4 overflow-hidden rounded shadow-xl sm:mb-6 sm:mt-16 print:m-0 print:shadow-none"
          style={{
            width: dimensions.width ? dimensions.width * scale : undefined,
            height: dimensions.height ? dimensions.height * scale : undefined,
          }}
        >
          <iframe
            ref={frameRef}
            title={title}
            src="/artboard/preview"
            style={{
              width: dimensions.width || `${pageSizeMap[format].width}mm`,
              height: dimensions.height || undefined,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              overflow: "hidden",
              border: 0,
            }}
          />
        </div>
      </div>

      <div className="flex justify-center py-6 opacity-50 sm:py-10 print:hidden">
        <Link to="/">
          <Button size="sm" variant="ghost" className="space-x-1.5 text-xs font-normal">
            <span>{t`Built with`}</span>
            <Icon size={12} />
            <span>{t`Izicvs`}</span>
          </Button>
        </Link>
      </div>

      <div className="fixed bottom-5 right-5 z-[60] print:hidden">
        <div className="flex flex-col items-center gap-y-2">
          <Button size="icon" onClick={onDownloadPdf}>
            {loading ? <CircleNotch size={20} className="animate-spin" /> : <FilePdf size={20} />}
          </Button>

          <ThemeSwitch />
        </div>
      </div>
    </div>
  );
};

export const publicLoader: LoaderFunction<ResumeDto> = async ({ params }) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const username = params.username!;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const slug = params.slug!;

    return await queryClient.fetchQuery({
      queryKey: ["resume", { username, slug }],
      queryFn: () => findResumeByUsernameSlug({ username, slug }),
    });
  } catch {
    return redirect("/");
  }
};
